import { useState, useEffect } from "react";
import {
  ConnectMetaMask,
  ConnectTrustWallet,
  ConnectWithOther,
} from "./walletConnectService";
import { Metamask, Trustwallet, Others } from "../assets";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { websiteURL } from "../config";
import { useHookstate } from "@hookstate/core";
import { metaMaskState } from "../hooks/store";

export const WalletConnection = () => {
  const metamaskState = useHookstate(metaMaskState);
  const [connectedAddress, setConnectedAddress] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [connectedWalletType, setConnectedWalletType] = useState(null);
  const [isTriggered, setIsTriggered] = useState(false);

  const navigate = useNavigate();

  // Check if we're on mobile
  const isMobileDevice = /android|iphone|kindle|ipad/i.test(navigator.userAgent);

  // Check URL parameters for wallet type after mobile deep link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const walletType = urlParams.get('wallet');
    if (walletType && !isTriggered) {
      setIsTriggered(true);
      handleWalletConnect(walletType, null, true);
    }
  }, [isTriggered]);

  // Check for existing connection
  useEffect(() => {
    const storedAddress = Cookies.get("connectedAddress");
    const storedWalletType = Cookies.get("connectedWalletType");
    const walletInfoData = localStorage.getItem("connectedWalletInfo");

    if (storedAddress && storedWalletType && walletInfoData) {
      const walletInfo = JSON.parse(walletInfoData);
      setConnectedAddress(storedAddress);
      setConnectedWalletType(storedWalletType);
      metamaskState.set({
        isConnected: true,
        address: storedAddress,
        walletIcon: walletInfo.walletIcon,
        walletName: walletInfo.walletType,
      });
    }
  }, []);

  const handleConnectWalletPress = () => {
    setModalOpen(true);
  };

  const handleWalletConnect = async (walletType, connectors, isTriggered) => {
    try {
      let result = null;

      if (walletType === "Metamask") {
        if (!isTriggered && isMobileDevice) {
          window.location.replace(
            `https://metamask.app.link/dapp/${websiteURL}?wallet=Metamask`
          );
          return { status: "redirect", error: "Redirecting" };
        } else {
          result = await ConnectMetaMask();
        }
      } else if (walletType === "TrustWallet") {
        if (!isTriggered && isMobileDevice) {
          window.location.replace(
            `https://link.trustwallet.com/open_url?coin_id=60&url=${websiteURL}?wallet=TrustWallet`
          );
          return { status: "redirect", error: "Redirecting" };
        } else {
          result = await ConnectTrustWallet();
        }
      } else if (walletType === "Other") {
        if (!isTriggered && isMobileDevice) {
          window.location.replace(
            `https://metamask.app.link/dapp/${websiteURL}?wallet=Other`
          );
          return { status: "redirect", error: "Redirecting" };
        } else {
          result = await ConnectWithOther(connectors);
        }
      }

      if (result?.status === "success") {
        // Store wallet info in localStorage
        const walletInfo = {
          walletType,
          walletIcon: getWalletImage(walletType),
        };
        localStorage.setItem("connectedWalletInfo", JSON.stringify(walletInfo));

        setConnectedAddress(result.address);
        setConnectedWalletType(walletType);
        setModalOpen(false);
        
        // Store connection info
        Cookies.set("connectedAddress", result.address, { expires: 30 });
        Cookies.set("connectedWalletType", walletType, { expires: 30 });
        
        // Update global state
        metamaskState.set({
          isConnected: true,
          address: result.address,
          walletIcon: walletInfo.walletIcon,
          walletName: walletInfo.walletType,
        });

        // Redirect to dashboard if needed
        if (walletType === "Metamask") {
          window.location.reload();
        } else if (!Cookies.get("connectedAddress")) {
          navigate("/dashboard/tickets");
        }

        return { status: "success", address: result.address };
      } else {
        return { status: "error", error: result?.error || "Failed to connect with wallet" };
      }
    } catch (error) {
      console.error("Wallet connection error:", error);
      setConnectedAddress(null);
      return { status: "error", error: error.message };
    }
  };

  const handleDisconnect = async () => {
    try {
      setConnectedAddress(null);
      setConnectedWalletType(null);
      Cookies.remove("connectedAddress");
      Cookies.remove("connectedWalletType");
      localStorage.removeItem("connectedWalletInfo");
      
      metamaskState.set({
        isConnected: false,
        address: null,
        walletIcon: null,
        walletName: null,
      });

      // Revoke wallet permissions
      if (window.ethereum) {
        await window.ethereum.request({
          method: "wallet_revokePermissions",
          params: [{ eth_accounts: {} }],
        });
      }

      navigate("/");
    } catch (error) {
      console.error("Error disconnecting wallet:", error);
    }
  };

  return {
    connectedAddress,
    isModalOpen,
    handleConnectWalletPress,
    handleWalletConnect,
    handleDisconnect,
    connectedWalletType,
    setModalOpen,
  };
};

export const getWalletImage = (walletType) => {
  switch (walletType) {
    case "Metamask":
      return Metamask;
    case "TrustWallet":
      return Trustwallet;
    default:
      return Others;
  }
};
